import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  RefreshCw,
  Flame,
  AlertTriangle,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Target,
  Zap,
  Clock,
  Shield,
  BarChart3,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Scanner() {
  const [scanResults, setScanResults] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [gettingAI, setGettingAI] = useState(false);
  const [activeTab, setActiveTab] = useState("opportunities");

  const fetchScanResults = useCallback(async () => {
    try {
      const [scanRes, statusRes] = await Promise.all([
        api.get("/scanner/opportunities?limit=20"),
        api.get("/monitor/realtime-status")
      ]);
      setScanResults(scanRes.data);
      setRealtimeStatus(statusRes.data);
    } catch (error) {
      console.error("Error fetching scan results:", error);
      toast.error("Failed to load market scan");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleScan = async () => {
    setScanning(true);
    try {
      await api.post("/scanner/trigger");
      await fetchScanResults();
      toast.success("Market scan complete!");
    } catch (error) {
      console.error("Error scanning:", error);
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleGetAIRecommendations = async () => {
    setGettingAI(true);
    try {
      const response = await api.get("/scanner/ai-recommendations");
      setAiRecommendations(response.data);
      toast.success("AI recommendations generated!");
    } catch (error) {
      console.error("Error getting AI recommendations:", error);
      toast.error("Failed to get AI recommendations");
    } finally {
      setGettingAI(false);
    }
  };

  useEffect(() => {
    fetchScanResults();
  }, [fetchScanResults]);

  const renderStockCard = (stock, type) => {
    const isHot = type === "hot";
    const cardClass = isHot ? "hot-stock" : "danger-stock";
    const badgeClass = isHot ? "badge-hot" : "badge-danger";
    
    return (
      <Card 
        key={stock.symbol} 
        className={`${cardClass} animate-slide-in`}
        data-testid={`${type}-stock-${stock.symbol}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {isHot ? (
                <Flame className="w-6 h-6 text-yellow-400 fire-icon" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-400" />
              )}
              <div>
                <p className="font-heading font-bold text-xl">{stock.symbol}</p>
                <p className="text-xs text-muted-foreground">{stock.name}</p>
              </div>
            </div>
            <Badge className={badgeClass}>
              {isHot ? "🔥 HOT" : "⚠️ DANGER"}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="p-2 bg-black/30 rounded">
              <p className="text-xs text-muted-foreground">Current</p>
              <p className="font-data font-bold text-lg">${stock.current_price}</p>
            </div>
            <div className="p-2 bg-black/30 rounded">
              <p className="text-xs text-muted-foreground">Confidence</p>
              <p className="font-data font-bold text-lg">{stock.confidence}%</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 bg-profit/20 rounded">
              <p className="text-xs text-muted-foreground">Entry</p>
              <p className="font-data text-sm text-profit">${stock.entry_price}</p>
            </div>
            <div className="text-center p-2 bg-info/20 rounded">
              <p className="text-xs text-muted-foreground">Target</p>
              <p className="font-data text-sm text-info">${stock.target_price}</p>
            </div>
            <div className="text-center p-2 bg-loss/20 rounded">
              <p className="text-xs text-muted-foreground">Stop</p>
              <p className="font-data text-sm text-loss">${stock.stop_loss}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 mb-2">
            {stock.patterns_detected?.map((pattern, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {pattern}
              </Badge>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">{stock.reasoning}</p>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <Badge variant="outline" className="text-xs">
              {stock.alert_level === "sloth" ? "🦥 Sloth" : "🐅 Tiger"}
            </Badge>
            <Badge variant="outline" className={`text-xs ${
              stock.risk_level === "low" ? "text-profit" : 
              stock.risk_level === "high" ? "text-loss" : "text-warning"
            }`}>
              {stock.risk_level?.toUpperCase()} RISK
            </Badge>
            <span className="text-xs text-profit font-data">
              +{stock.potential_gain_pct}% potential
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="scanner-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 skeleton rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scanner-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">
            AI STOCK SCANNER
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Automatic market analysis • Trading & investment recommendations
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 px-2 py-1 bg-profit/20 border border-profit/30 rounded text-xs">
            <div className="w-2 h-2 bg-profit rounded-full animate-pulse" />
            <span className="text-profit font-data">LIVE</span>
          </div>
          <Badge variant="outline" className="gap-1 py-1 text-xs">
            <Clock className="w-3 h-3" />
            Price: 30s | Patterns: 2min
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleScan}
            disabled={scanning}
            className="gap-2"
            data-testid="scan-btn"
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} />
            Scan Now
          </Button>
          <Button
            size="sm"
            onClick={handleGetAIRecommendations}
            disabled={gettingAI}
            className="gap-2 bg-ai hover:bg-ai/80"
            data-testid="ai-recommendations-btn"
          >
            <Sparkles className={`w-4 h-4 ${gettingAI ? "animate-spin" : ""}`} />
            AI Trading Algorithm
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-info" />
              <div>
                <p className="text-xs text-muted-foreground">Stocks Scanned</p>
                <p className="font-data font-bold text-xl">{scanResults?.scanned_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-warning" />
              <div>
                <p className="text-xs text-muted-foreground">Signals Found</p>
                <p className="font-data font-bold text-xl">{scanResults?.signals_found || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark border-l-2 border-l-yellow-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Flame className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-xs text-muted-foreground">Hot Stocks</p>
                <p className="font-data font-bold text-xl text-yellow-400">
                  {scanResults?.hot_stocks?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark border-l-2 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-xs text-muted-foreground">Danger Stocks</p>
                <p className="font-data font-bold text-xl text-red-400">
                  {scanResults?.danger_stocks?.length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="opportunities" className="font-heading gap-2" data-testid="tab-opportunities">
            <Target className="w-4 h-4" />
            OPPORTUNITIES
          </TabsTrigger>
          <TabsTrigger value="ai-algo" className="font-heading gap-2" data-testid="tab-ai-algo">
            <Sparkles className="w-4 h-4" />
            AI ALGORITHM
          </TabsTrigger>
        </TabsList>

        <TabsContent value="opportunities" className="mt-6">
          {/* Hot Stocks - About to Pop */}
          {scanResults?.hot_stocks?.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-6 h-6 text-yellow-400 fire-icon" />
                <h2 className="font-heading font-bold text-xl text-yellow-400">
                  HOT STOCKS - ABOUT TO POP 🚀
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scanResults.hot_stocks.map((stock) => renderStockCard(stock, "hot"))}
              </div>
            </div>
          )}

          {/* Danger Stocks - About to Crash */}
          {scanResults?.danger_stocks?.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <h2 className="font-heading font-bold text-xl text-red-400">
                  DANGER STOCKS - MAY CRASH ⚠️
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scanResults.danger_stocks.map((stock) => renderStockCard(stock, "danger"))}
              </div>
            </div>
          )}

          {/* All Opportunities */}
          {scanResults?.all_opportunities?.length > 0 && (
            <div>
              <h2 className="font-heading font-bold text-xl mb-4">ALL SIGNALS</h2>
              <div className="space-y-3">
                {scanResults.all_opportunities.map((stock, idx) => (
                  <Card key={idx} className="card-dark" data-testid={`signal-${stock.symbol}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 flex items-center justify-center ${
                            stock.is_bullish ? "bg-profit/20" : "bg-loss/20"
                          }`}>
                            {stock.is_bullish ? (
                              <TrendingUp className="w-5 h-5 text-profit" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-loss" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-heading font-bold">{stock.symbol}</p>
                              <Badge variant="outline" className="text-xs">
                                {stock.signal_type?.replace(/_/g, ' ')}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {stock.alert_level === "sloth" ? "🦥" : "🐅"}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{stock.reasoning}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-data font-bold">${stock.current_price}</p>
                          <p className="text-xs text-muted-foreground">
                            Confidence: {stock.confidence}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {!scanResults?.all_opportunities?.length && (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No signals detected</p>
              <p className="text-sm text-muted-foreground mt-1">Click "Scan Now" to analyze the market</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai-algo" className="mt-6">
          {aiRecommendations ? (
            <div className="space-y-6">
              {/* Market Sentiment */}
              <Card className={`card-dark border-l-4 ${
                aiRecommendations.market_sentiment === 'bullish' ? 'border-l-profit' :
                aiRecommendations.market_sentiment === 'bearish' ? 'border-l-loss' : 'border-l-warning'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Market Sentiment</p>
                      <p className={`font-heading font-bold text-2xl ${
                        aiRecommendations.market_sentiment === 'bullish' ? 'text-profit' :
                        aiRecommendations.market_sentiment === 'bearish' ? 'text-loss' : 'text-warning'
                      }`}>
                        {aiRecommendations.market_sentiment?.toUpperCase()}
                      </p>
                    </div>
                    <Sparkles className="w-8 h-8 text-ai" />
                  </div>
                  {aiRecommendations.overall_advice && (
                    <p className="text-sm text-muted-foreground mt-2">{aiRecommendations.overall_advice}</p>
                  )}
                </CardContent>
              </Card>

              {/* Trading Recommendations */}
              {aiRecommendations.trading_recommendations?.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-warning" />
                    TRADING RECOMMENDATIONS
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {aiRecommendations.trading_recommendations.map((rec, idx) => (
                      <Card key={idx} className={`card-dark ${
                        rec.action === 'BUY' ? 'border-l-2 border-l-profit' :
                        rec.action === 'SELL' || rec.action === 'SHORT' ? 'border-l-2 border-l-loss' : ''
                      }`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-heading font-bold text-lg">{rec.symbol}</span>
                              <Badge className={
                                rec.action === 'BUY' ? 'bg-profit/20 text-profit' :
                                'bg-loss/20 text-loss'
                              }>
                                {rec.action}
                              </Badge>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {rec.strategy?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="text-center p-2 bg-background rounded">
                              <p className="text-xs text-muted-foreground">Entry</p>
                              <p className="font-data text-sm">{rec.entry_zone}</p>
                            </div>
                            <div className="text-center p-2 bg-background rounded">
                              <p className="text-xs text-muted-foreground">Target</p>
                              <p className="font-data text-sm text-profit">{rec.target}</p>
                            </div>
                            <div className="text-center p-2 bg-background rounded">
                              <p className="text-xs text-muted-foreground">Stop</p>
                              <p className="font-data text-sm text-loss">{rec.stop_loss}</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">{rec.reasoning}</p>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                            <Badge variant="outline" className={`text-xs ${
                              rec.urgency === 'immediate' ? 'text-loss' :
                              rec.urgency === 'wait_for_dip' ? 'text-warning' : 'text-muted-foreground'
                            }`}>
                              {rec.urgency?.replace(/_/g, ' ')?.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Position: {rec.position_size}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Long-Term Picks */}
              {aiRecommendations.long_term_picks?.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-info" />
                    LONG-TERM INVESTMENT PICKS
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {aiRecommendations.long_term_picks.map((pick, idx) => (
                      <Card key={idx} className="card-dark border-l-2 border-l-info">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-heading font-bold text-lg">{pick.symbol}</span>
                            <Badge variant="outline" className={`text-xs ${
                              pick.risk_rating === 'low' ? 'text-profit' :
                              pick.risk_rating === 'high' ? 'text-loss' : 'text-warning'
                            }`}>
                              {pick.risk_rating?.toUpperCase()} RISK
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{pick.thesis}</p>
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {pick.time_horizon}
                          </Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Stocks to Avoid */}
              {aiRecommendations.stocks_to_avoid?.length > 0 && (
                <Card className="card-dark border-l-4 border-l-loss">
                  <CardContent className="p-4">
                    <h3 className="font-heading font-bold text-loss mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      STOCKS TO AVOID
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {aiRecommendations.stocks_to_avoid.map((stock, idx) => (
                        <Badge key={idx} variant="outline" className="text-loss border-loss/50">
                          {stock}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 text-ai mx-auto mb-4" />
              <p className="text-muted-foreground">No AI recommendations yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "AI Trading Algorithm" to generate recommendations
              </p>
              <Button
                onClick={handleGetAIRecommendations}
                disabled={gettingAI}
                className="mt-4 gap-2 bg-ai hover:bg-ai/80"
              >
                <Sparkles className={`w-4 h-4 ${gettingAI ? "animate-spin" : ""}`} />
                Generate AI Recommendations
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
